import { useEffect, useState } from 'react';
import { PageHeader } from '../components/PageHeader';
import { Button, Card } from '../components/ui';

import { useAuth } from '../contexts/AuthContext';
import { useT, userError } from '../i18n/messages';
import { ApiError, billingApi, type OverviewResponse } from '../lib/api';

export function OverviewPage() {
  const { tokens } = useAuth();
  const t = useT();
  const [state, setState] = useState<{
    status: 'loading' | 'success' | 'error';
    data: OverviewResponse | null;
    error: ApiError | null;
  }>({ status: 'loading', data: null, error: null });
  const [actionState, setActionState] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!tokens) {
      return;
    }
    setState({ status: 'loading', data: null, error: null });
    billingApi
      .getOverview(tokens.access_token)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'success', data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            status: 'error',
            data: null,
            error:
              error instanceof ApiError
                ? error
                : new ApiError(String(error), 500, 'request_failed'),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tokens]);

  if (state.status === 'loading') {
    return <section className="panel page-panel">{t('billing.resource.loading')}</section>;
  }

  if (state.status === 'error' || !state.data) {
    return <section className="panel page-panel">{userError(state.error, t)}</section>;
  }

  const cards = [
    ['billing.overview.totalApplications', state.data.totals.applications],
    ['billing.overview.totalCustomers', state.data.totals.billing_customers],
    ['billing.overview.totalSubscriptions', state.data.totals.active_subscriptions],
    ['billing.overview.totalFailedDeliveries', state.data.totals.failed_deliveries],
  ] as const;

  async function rerunReconciliation() {
    if (!tokens) {
      return;
    }
    setActionState(t('billing.ui.running.reconciliation'));
    const result = await billingApi.rerunReconciliation(tokens.access_token, {});
    setActionState(t('billing.recovery.repaired', { count: result.repaired_count }));
  }

  async function replayDeadLetters() {
    if (!tokens) {
      return;
    }
    setActionState(t('billing.ui.replaying.dead.lettered.deliveries'));
    const result = await billingApi.replayDeadLetters(tokens.access_token, {
      replay_scope: 'all_dead_lettered',
    });
    setActionState(t('billing.recovery.replayed', { count: result.replayed_count }));
  }

  return (
    <section className="page" data-testid="billing-overview-page">
      <PageHeader title={t('billing.overview.title')} />
      <div className="stat-grid">
        {cards.map(([label, value]) => (
          <Card key={label} className="stat-card">
            <span>{t(label)}</span>
            <strong>{value}</strong>
          </Card>
        ))}
      </div>
      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.column.actions')}</h3>
        </div>
        <div className="settings-subgrid">
          <Button onClick={rerunReconciliation} type="button">
            {t('billing.ui.re.run.reconciliation')}
          </Button>
          <Button onClick={replayDeadLetters} type="button" variant="secondary">
            {t('billing.ui.replay.dead.letters')}
          </Button>
        </div>
        <p>{actionState ?? t('billing.ui.run.recovery.actions.for.billing.data')}</p>
      </Card>
      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.overview.providerStatus')}</h3>
        </div>
        {state.data.provider_status.length === 0 ? (
          <p>{t('billing.overview.emptyProviders')}</p>
        ) : (
          <ul className="event-list">
            {state.data.provider_status.map((provider) => (
              <li key={provider.provider_key}>
                <strong>{String(provider.provider_key)}</strong>
                <span>
                  {provider.enabled ? t('billing.status.enabled') : t('billing.status.disabled')}
                </span>
                <span>{String(provider.status)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card className="panel page-panel">
        <div className="panel-heading">
          <h3>{t('billing.overview.recentEvents')}</h3>
        </div>
        {state.data.recent_events.length === 0 ? (
          <p>{t('billing.overview.emptyEvents')}</p>
        ) : (
          <ul className="event-list">
            {state.data.recent_events.map((event, index) => (
              <li key={String(event.id ?? index)}>
                <strong>{String(event.event_type ?? '—')}</strong>
                <span>{String(event.record_kind ?? '—')}</span>
                <span>{String(event.processing_status ?? '—')}</span>
                <span>{String(event.occurred_at ?? '—')}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}
